import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import api from '../../lib/api';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface Persona {
  id: string;
  name: string;
  jobTitle: string;
  type: string;
  agenda: string;
  typicalObjections: string;
  voice: string | null;
  voiceProvider: string | null;
  gender?: string | null;
  videoAvatarId?: string | null;
}

interface VideoAvatar {
  id: string;
  name: string;
  previewImageUrl: string | null;
  gender: string | null;
}

interface VoiceOption {
  id: string;
  label: string;
}

interface VoicesResponse {
  provider: string;
  voices: VoiceOption[];
}

const AUTO_VOICE = '';

interface PersonaForm {
  name: string;
  jobTitle: string;
  type: string;
  agenda: string;
  typicalObjections: string;
  voice: string;
  gender: string;
  videoAvatarId: string;
}

const emptyForm = (): PersonaForm => ({
  name: '',
  jobTitle: '',
  type: 'champion',
  agenda: '',
  typicalObjections: '',
  voice: AUTO_VOICE,
  gender: '',
  videoAvatarId: '',
});

const fieldIds = {
  name: 'admin-persona-name',
  nameHelp: 'admin-persona-name-help',
  jobTitle: 'admin-persona-job-title',
  jobTitleHelp: 'admin-persona-job-title-help',
  type: 'admin-persona-type',
  typeHelp: 'admin-persona-type-help',
  voice: 'admin-persona-voice',
  voiceHelp: 'admin-persona-voice-help',
  agenda: 'admin-persona-agenda',
  agendaHelp: 'admin-persona-agenda-help',
  objections: 'admin-persona-objections',
  objectionsHelp: 'admin-persona-objections-help',
} as const;

export function AdminPersonasPage() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PersonaForm>(emptyForm());
  const [voiceProvider, setVoiceProvider] = useState<string>('openai');
  const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>([]);
  const [personaIdToDelete, setPersonaIdToDelete] = useState<string | null>(null);
  const [videoAvatars, setVideoAvatars] = useState<VideoAvatar[]>([]);

  useEffect(() => {
    loadPersonas();
    api
      .get<VoicesResponse>('/practice/tts/voices')
      .then((res) => {
        if (res.data?.provider) setVoiceProvider(res.data.provider);
        if (Array.isArray(res.data?.voices)) setVoiceOptions(res.data.voices);
      })
      .catch(() => {});
    api
      .get<VideoAvatar[]>('/admin/video-avatars')
      .then((res) =>
        setVideoAvatars(
          res.data.filter(
            (a: VideoAvatar & { orgId?: string | null }) =>
              !(a as unknown as { orgId: string | null }).orgId,
          ),
        ),
      )
      .catch(() => {});
  }, []);

  const loadPersonas = () => {
    api
      .get('/admin/personas')
      .then((res) => setPersonas(res.data))
      .catch(() => {});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        jobTitle: form.jobTitle,
        type: form.type,
        agenda: form.agenda,
        typicalObjections: form.typicalObjections,
        voice: form.voice || null,
        voiceProvider: form.voice ? voiceProvider : null,
        gender: form.gender || null,
        videoAvatarId: form.videoAvatarId || null,
      };
      if (editingId) {
        await api.patch(`/admin/personas/${editingId}`, payload);
      } else {
        await api.post('/admin/personas', payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      loadPersonas();
    } catch (err) {
      console.error('Failed to save persona', err);
      Sentry.captureException(err);
    }
  };

  const handleEdit = (p: Persona) => {
    setForm({
      name: p.name,
      jobTitle: p.jobTitle,
      type: p.type,
      agenda: p.agenda,
      typicalObjections: p.typicalObjections,
      voice:
        p.voice && (!p.voiceProvider || p.voiceProvider === voiceProvider) ? p.voice : AUTO_VOICE,
      gender: p.gender ?? '',
      videoAvatarId: p.videoAvatarId ?? '',
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (!personaIdToDelete) return;
    const id = personaIdToDelete;
    setPersonaIdToDelete(null);
    await api.delete(`/admin/personas/${id}`);
    loadPersonas();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Persona Catalog</h1>
          <p className="text-gray-600 mt-1">
            Platform-wide personas available to all organizations.
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            setForm(emptyForm());
          }}
          className="bg-red-700 text-white px-4 py-2 rounded-md font-medium hover:bg-red-800"
        >
          New Persona
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg border mb-8">
          <h2 className="text-lg font-semibold mb-2">{editingId ? 'Edit' : 'Create'} Persona</h2>
          <p className="text-sm text-gray-600 mb-4">
            These fields define how the practice buyer AI speaks and reacts. They become part of the
            instructions the model follows whenever it plays this buyer during a session.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor={fieldIds.name}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Name
                </label>
                <input
                  id={fieldIds.name}
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  aria-describedby={fieldIds.nameHelp}
                  className="w-full px-3 py-2 border rounded-md"
                />
                <p id={fieldIds.nameHelp} className="text-xs text-gray-500 mt-1">
                  Used as the buyer&apos;s name in the AI instructions. Reps see this name in the
                  practice session.
                </p>
              </div>
              <div>
                <label
                  htmlFor={fieldIds.jobTitle}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Job title
                </label>
                <input
                  id={fieldIds.jobTitle}
                  type="text"
                  value={form.jobTitle}
                  onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                  required
                  aria-describedby={fieldIds.jobTitleHelp}
                  className="w-full px-3 py-2 border rounded-md"
                />
                <p id={fieldIds.jobTitleHelp} className="text-xs text-gray-500 mt-1">
                  Sets the buyer&apos;s role in the AI instructions; shapes tone and priorities
                  together with buyer archetype.
                </p>
              </div>
            </div>
            <div>
              <label
                htmlFor={fieldIds.type}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Buyer archetype
              </label>
              <select
                id={fieldIds.type}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                aria-describedby={fieldIds.typeHelp}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="champion">Champion</option>
                <option value="blocker">Blocker</option>
                <option value="skeptic">Skeptic</option>
                <option value="economic_buyer">Economic Buyer</option>
                <option value="technical_evaluator">Technical Evaluator</option>
                <option value="end_user">End User</option>
              </select>
              <p id={fieldIds.typeHelp} className="text-xs text-gray-500 mt-1">
                One of six fixed stakeholder roles. Included in the AI instructions so the buyer
                stays consistent with that role.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender (optional)
                </label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Not specified</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="nonbinary">Non-binary</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video avatar (optional)
                </label>
                <select
                  value={form.videoAvatarId}
                  onChange={(e) => setForm({ ...form, videoAvatarId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">None</option>
                  {videoAvatars.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.gender ? ` (${a.gender})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Assign a video avatar for live video call sessions. Manage avatars on the{' '}
                  <a href="/admin/video-avatars" className="text-blue-600 hover:underline">
                    Video Avatars page
                  </a>
                  .
                </p>
              </div>
            </div>
            <div>
              <label
                htmlFor={fieldIds.voice}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Voice ({voiceProvider})
              </label>
              <select
                id={fieldIds.voice}
                value={form.voice}
                onChange={(e) => setForm({ ...form, voice: e.target.value })}
                aria-describedby={fieldIds.voiceHelp}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value={AUTO_VOICE}>Auto (assigned per session)</option>
                {voiceOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </select>
              <p id={fieldIds.voiceHelp} className="text-xs text-gray-500 mt-1">
                Text-to-speech voice for video call practice only (not the model&apos;s reasoning).
                Auto assigns a distinct voice per persona in the same session so two buyers never
                share a voice.
              </p>
            </div>
            <div>
              <label
                htmlFor={fieldIds.agenda}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                What they care about (agenda)
              </label>
              <textarea
                id={fieldIds.agenda}
                value={form.agenda}
                onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                rows={2}
                required
                aria-describedby={fieldIds.agendaHelp}
                className="w-full px-3 py-2 border rounded-md"
              />
              <p id={fieldIds.agendaHelp} className="text-xs text-gray-500 mt-1">
                Fed to the model as this buyer&apos;s priorities in the deal—drives what they push
                for in the conversation.
              </p>
            </div>
            <div>
              <label
                htmlFor={fieldIds.objections}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Typical objections
              </label>
              <textarea
                id={fieldIds.objections}
                value={form.typicalObjections}
                onChange={(e) => setForm({ ...form, typicalObjections: e.target.value })}
                rows={3}
                required
                aria-describedby={fieldIds.objectionsHelp}
                className="w-full px-3 py-2 border rounded-md"
              />
              <p id={fieldIds.objectionsHelp} className="text-xs text-gray-500 mt-1">
                Themes the AI should use when pushing back; the model draws from these rather than
                agreeing too easily.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-red-700 text-white px-6 py-2 rounded-md font-medium hover:bg-red-800"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="border text-gray-700 px-6 py-2 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {personas.map((p) => (
          <div key={p.id} className="bg-white p-5 rounded-lg border">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-gray-900">{p.name}</h3>
                <p className="text-sm text-gray-500">{p.jobTitle}</p>
              </div>
              <span className="text-xs px-2 py-1 bg-gray-100 rounded">{p.type}</span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{p.agenda}</p>
            <p className="text-xs text-gray-400 mb-3">{p.typicalObjections}</p>
            {p.voice && (
              <p className="text-xs text-gray-500 mb-2">
                Voice: <span className="font-medium">{p.voice}</span>
                {p.voiceProvider ? ` (${p.voiceProvider})` : ''}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(p)}
                className="text-sm text-blue-600 hover:underline"
              >
                Edit
              </button>
              <button
                onClick={() => setPersonaIdToDelete(p.id)}
                className="text-sm text-red-600 hover:underline"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={personaIdToDelete !== null}
        title="Delete persona?"
        description="Delete this persona? It will be removed from the platform catalog."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPersonaIdToDelete(null)}
      />
    </div>
  );
}
